import {
  type CoreFiltersFrontendState,
  transformFrontendStateToQueryArgs,
  unsetNonFilterableDimensionAttributes,
} from '@autone/redux/core';
import { STABLE_ARRAY } from '@autone/utils';
import { get, set, unset } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { type Get, type OmitDeep, type Paths } from 'type-fest';

import { useCoreGeneralFiltersContext } from '../contexts/core-general-filters.context';

type CoreProductFiltersFrontendState = Pick<
  CoreFiltersFrontendState,
  | 'product'
  | 'catalogue'
  | 'product_groups'
  | 'location_cluster'
  | 'sku'
  | 'size'
  | 'size_run'
>;

export type CoreProductGeneralFiltersDimensionAttributePaths = Paths<
  CoreProductFiltersFrontendState,
  { depth: 1 }
>;

/**
 * Context hook binding all core product filters together.
 */
export const useCoreProductGeneralFiltersContext = <
  P extends CoreProductGeneralFiltersDimensionAttributePaths,
>(
  dimensionAggregationPath: P,
) => {
  const [coreGeneralFilters, setCoreGeneralFilters] =
    useCoreGeneralFiltersContext();

  const coreProductFiltersQueryArg = useMemo(() => {
    const {
      product,
      catalogue,
      product_groups,
      location_cluster,
      sku,
      size,
      size_run,
    } = structuredClone(coreGeneralFilters);
    const coreProductFilters = unsetNonFilterableDimensionAttributes({
      product,
      catalogue,
      product_groups,
      location_cluster,
      sku,
      size,
      size_run,
    });
    unset(coreProductFilters, dimensionAggregationPath);
    // need to typecast because `unset` is a mutable operation
    return transformFrontendStateToQueryArgs(
      coreProductFilters as OmitDeep<
        typeof coreProductFilters,
        typeof dimensionAggregationPath
      >,
    );
  }, [coreGeneralFilters, dimensionAggregationPath]);

  // Get the value of the selected dimension aggregation path.
  const dimensionAttributeValue = useMemo(() => {
    return get(coreGeneralFilters, dimensionAggregationPath) ?? STABLE_ARRAY;
  }, [coreGeneralFilters, dimensionAggregationPath]);

  // Set the value of the selected dimension aggregation path.
  const setDimensionAttributeValue = useCallback(
    (value: Get<CoreProductFiltersFrontendState, P>) => {
      const newValue =
        Array.isArray(value) && value.length === 0 ? undefined : value;
      setCoreGeneralFilters((draft) => {
        set(draft, dimensionAggregationPath, newValue);
      });
    },
    [setCoreGeneralFilters, dimensionAggregationPath],
  );

  return {
    coreProductFiltersQueryArg,
    dimensionAttributeValue,
    setDimensionAttributeValue,
  };
};
